import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { usePanelContext } from "../panelContext";
import type {
  AppNotification,
  CashoutRequestInput,
  CashoutRequestResponse,
  EarningsFilters,
  Profile,
  SignupInput,
  UpdateMemberCheckInInput,
  UpdateProfileInput,
} from "../types";
import { applyBalanceToPanelist } from "../utils";
import {
  deactivateAccount,
  fetchBalance,
  fetchEarningsHistory,
  fetchFile,
  fetchFlexNotificationUrl,
  fetchNotification,
  fetchPanelFields,
  fetchPanelist,
  fetchPreTask,
  fetchProfile,
  fetchPublicPanel,
  fetchRecentNotifications,
  fetchSessionContext,
  fetchTask,
  fetchTasks,
  markNotificationRead,
  requestCashout,
  resendSignupVerification,
  sendTaskEvent,
  signOut,
  signUpPanelist,
  TaskEvent,
  updateMemberCheckIn,
  updateProfile,
  updateSessionContext,
  updateTaskFingerprint,
} from "./fetchers";
import { queryKeys } from "./queryKeys";

interface PortalQueryOptions {
  enabled?: boolean;
}

const BALANCE_REFETCH_INTERVAL_MS = 15_000;

export const usePanelId = () => {
  const { panelId } = usePanelContext();
  return panelId;
};
export function usePanelist(options: PortalQueryOptions = {}) {
  const enabled = options.enabled ?? true;
  const panelistQuery = useQuery({
    enabled,
    queryFn: fetchPanelist,
    queryKey: queryKeys.panelist,
  });
  const publicPanelQuery = usePublicPanel({ enabled });
  const balanceQuery = useBalance({ enabled });

  return {
    ...panelistQuery,
    data: applyBalanceToPanelist(
      panelistQuery.data,
      balanceQuery.data,
      publicPanelQuery.data,
    ),
  };
}

export function useBalance(options: PortalQueryOptions = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryFn: fetchBalance,
    queryKey: queryKeys.balance,
    // refetchInterval: BALANCE_REFETCH_INTERVAL_MS,
    staleTime: BALANCE_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}

export function usePublicPanel(options: PortalQueryOptions = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryFn: () => fetchPublicPanel(),
    queryKey: queryKeys.publicPanel(),
  });
}

export function useSessionContext(
  input?: { panelId?: number } | string,
  options: PortalQueryOptions = {},
) {
  const panelId = typeof input === "object" ? input?.panelId : undefined;

  return useQuery({
    enabled: options.enabled ?? true,
    queryFn: () => fetchSessionContext(panelId),
    queryKey: queryKeys.sessionContext(panelId),
    refetchOnWindowFocus: true,
  });
}

export function useUpdateSessionContext(email: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (panelId: number) => updateSessionContext(panelId),
    onSuccess: (sessionContext) => {
      queryClient.setQueryData(
        queryKeys.sessionContext(sessionContext.activePanelId),
        sessionContext,
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.panelist });
      void queryClient.invalidateQueries({ queryKey: queryKeys.balance });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasks() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      void queryClient.invalidateQueries({ queryKey: queryKeys.panelFields });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

export function useTasks(
  rejectedTaskIds: string[] = [],
  forceRefreshKey: number = 0,
  enabled: boolean = true,
) {
  return useQuery({
    queryFn: () => fetchTasks(rejectedTaskIds, !!forceRefreshKey),
    queryKey: queryKeys.tasks(rejectedTaskIds, forceRefreshKey),
    enabled,
  });
}
export function useTask(taskId: string | undefined) {
  return useQuery({
    enabled: Boolean(taskId),
    queryFn: () => fetchTask(taskId as string),
    queryKey: queryKeys.task(taskId ?? ""),
  });
}

export function useFile(fileId: string | undefined) {
  return useQuery({
    enabled: Boolean(fileId),
    queryFn: () => fetchFile(fileId as string),
    queryKey: queryKeys.file(fileId ?? ""),
  });
}

export function usePreTask(
  taskId: string | undefined,
  options: PortalQueryOptions = {},
) {
  return useQuery({
    enabled: (options.enabled ?? true) && Boolean(taskId),
    queryFn: () => fetchPreTask(taskId as string),
    queryKey: queryKeys.taskPre(taskId ?? ""),
  });
}

export function useEarningsHistory(filters: EarningsFilters) {
  return useQuery({
    queryFn: () => fetchEarningsHistory(filters),
    queryKey: queryKeys.earningsHistory(filters),
  });
}

export function useProfile() {
  return useQuery({
    queryFn: fetchProfile,
    queryKey: queryKeys.profile,
  });
}

export function useProfileFields() {
  return useQuery({
    queryFn: () => fetchPanelFields(),
    queryKey: queryKeys.panelFields,
  });
}

export function useSignupFields(panelId: number | undefined, enabled = true) {
  return useQuery({
    enabled,
    queryFn: () => fetchPanelFields(panelId),
    queryKey: queryKeys.panelFields,
    select: (data) => ({
      sections: data.sections
        .map((s) => ({
          ...s,
          fields: s.fields.filter((f) => !f.readOnly && f.required),
        }))
        .filter((s) => !!s.fields.length),
    }),
  });
}

export function useRecentNotifications(enabled = true) {
  return useQuery({
    enabled,
    queryFn: fetchRecentNotifications,
    queryKey: queryKeys.notifications,
    staleTime: 60_000,
  });
}
export function useNotification(notificationId: string | undefined) {
  return useQuery({
    enabled: Boolean(notificationId),
    queryFn: () => fetchNotification(notificationId as string),
    queryKey: queryKeys.notification(notificationId ?? ""),
  });
}
export const useFlexNotificationUrl = ({
  domain,
  itemId,
}: {
  domain: string | null;
  itemId: number | null;
}) => {
  return useQuery({
    enabled: Boolean(domain && itemId),
    queryFn: () => fetchFlexNotificationUrl(domain!, itemId!),
    queryKey: queryKeys.flexNotificationUrl(domain!, itemId!),
  });
};

export const useMarkNotificationRead = () =>
  useMutation({
    mutationFn: (ids: string[]) => markNotificationRead(ids),
    onMutate: (ids, context) => {
      context.client.setQueryData<AppNotification[]>(
        queryKeys.notifications,
        (prev) => {
          return prev?.map((notification) => {
            if (ids.includes(notification.id)) {
              return { ...notification, readDate: new Date() };
            }
            return notification;
          });
        },
      );
    },
    onError: (err, ids, _, context) => {
      context.client.setQueryData<AppNotification[]>(
        queryKeys.notifications,
        (prev) => {
          return prev?.map((notification) => {
            if (ids.includes(notification.id)) {
              return { ...notification, readDate: null };
            }
            return notification;
          });
        },
      );
    },
  });
export function useRequestCashout(
  props?: Partial<
    UseMutationOptions<CashoutRequestResponse, Error, CashoutRequestInput>
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestCashout,
    ...props,
    onSuccess: (data, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.panelist });
      void queryClient.invalidateQueries({ queryKey: queryKeys.balance });
      return props?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateProfileInput) => updateProfile(input),
    onMutate: (input) => {
      const previousProfile = queryClient.getQueryData<Profile>(
        queryKeys.profile,
      );
      console.log("useUpdateProfile", input, previousProfile);
      if (previousProfile) {
        queryClient.setQueryData(queryKeys.profile, {
          ...previousProfile,
          ...input,
          personal: {
            ...previousProfile.personal,
            email: input.email,
            phone: input.phone,
          },
          commsPrefs: {
            ...previousProfile.commsPrefs,
            ...(input.commsPrefs ?? {}),
          },
          sections: previousProfile.sections?.map((section) => ({
            ...section,
            fields: section.fields.map((field) => ({
              ...field,
              value:
                input.profileFields?.[field.key] === undefined
                  ? field.value
                  : input.profileFields[field.key],
            })),
          })),
        });
      }
      return { previousProfile };
    },
    onError: (_error, _input, context) => {
      if (context?.previousProfile) {
        console.log("useUpdateProfile error", _error, context.previousProfile);
        queryClient.setQueryData(queryKeys.profile, context.previousProfile);
      }
    },
  });
}

export function useUpdateMemberCheckIn(checkInId: number | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateMemberCheckInInput) =>
      updateMemberCheckIn(checkInId as number, input),
    onSuccess: (_data) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.panelist });
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useSignUpPanelist(panelId: number | undefined) {
  return useMutation({
    mutationFn: (input: SignupInput) => signUpPanelist(panelId, input),
  });
}

export function useResendSignupVerification() {
  return useMutation({
    mutationFn: resendSignupVerification,
  });
}

export function useSignOut() {
  return useMutation({
    mutationFn: signOut,
  });
}

export function useDeactivateAccount() {
  return useMutation({
    mutationFn: deactivateAccount,
  });
}
export function useUpdateTaskFingerprint() {
  return useMutation({
    mutationFn: updateTaskFingerprint,
  });
}

export type HolidayCountry = {
  countryCode: string; // ISO 3166-1 alpha-2
  name: string;
};

const fetchHolidayCountries = async (): Promise<HolidayCountry[]> => {
  const response = await fetch(
    "https://date.nager.at/api/v3/AvailableCountries",
  );

  if (!response.ok) {
    throw new Error("Failed to fetch holiday countries");
  }

  return response.json();
};

export const useCountryCodes = <T = HolidayCountry[]>(
  props: Partial<UseQueryOptions<HolidayCountry[], Error, T>> = {},
) => {
  return useQuery<HolidayCountry[], Error, T>({
    queryKey: ["holiday-countries"],

    queryFn: fetchHolidayCountries,

    staleTime: Infinity,
    retry: 1,

    refetchOnWindowFocus: false,

    ...props,
  });
};
export const useCountries = useCountryCodes;
export const useSendTaskEvent = (
  props?: Partial<
    UseMutationOptions<
      void,
      Error,
      {
        event: TaskEvent;
        taskId?: string;
      }
    >
  >,
) => {
  return useMutation({
    mutationFn: async ({
      event,
      taskId,
    }: {
      event: TaskEvent;
      taskId?: string;
    }) => (taskId ? sendTaskEvent(taskId, event) : undefined),
    ...props,
  });
};
